/**
 * VideoCallView.jsx — LiveKit-based video/voice call component
 *
 * Architecture:
 *   1. Fetches token from POST /api/v1/agent/token
 *   2. Connects to the LiveKit room (user mic → agent audio)
 *   3. Subscribes to all remote AUDIO tracks (agent voice)
 *   4. If the agent publishes a VIDEO track (avatar), renders it via
 *      AvatarView — which is just a <video> element showing the remote track.
 *   5. Falls back to a pulsing-circle voice-only UI when no video track exists.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Room, RoomEvent, Track } from 'livekit-client';
import { Phone } from 'lucide-react';
import authService from '../../services/authService';
import AvatarView from './AvatarView';
import { playRingingOutSound, stopRingingSound } from '../../utils/soundUtils';

// ─── Sound effects ──────────────────────────────────────────────────────────

const createSafeAudio = (src) => {
  if (typeof Audio === 'undefined') return null;
  try {
    const audio = new Audio(src);
    audio.preload = 'metadata';
    audio.addEventListener('error', () => console.debug(`Audio not available: ${src}`));
    return audio;
  } catch { return null; }
};

const endCallSound = createSafeAudio('/sounds/end-call.mp3');

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDuration = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
};

// ─── Component ──────────────────────────────────────────────────────────────

export default function VideoCallView({
  applicationId,
  brandId,
  contactName = 'Cara',
  onCallEnd,
  enableSoundEffects = false,
  audioOnly = true,
}) {
  // State
  const [status, setStatus] = useState('connecting'); // connecting | connected | ended
  const [duration, setDuration] = useState(0);
  const [voiceState, setVoiceState] = useState('idle'); // idle | listening | speaking | thinking
  const [avatarReady, setAvatarReady] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [videoHidden, setVideoHidden] = useState(audioOnly);
  const [error, setError] = useState(null);

  // Audio output device picker
  // Global audio device preferences from Redux (set via AudioDevicePopover)
  const globalMicId = useSelector((state) => state.audio.selectedMicId);
  const globalSpeakerId = useSelector((state) => state.audio.selectedSpeakerId);
  // Per-config LiveKit overrides (null unless a config supplies a `livekit`
  // block — so this is inert for every existing Pega/Cara config).
  const livekitConfig = useSelector((state) => state.channel.livekit);
  // User-selectable spoken language (Settings popover), falling back to browser locale.
  const selectedLocale = useSelector((state) => state.audio.selectedLocale);

  // User personalization attributes from Cognito (e.g. given_name, family_name, email)
  const userAttributes = useSelector((state) => state.auth.attributes);

  // Personalization from config/brand:
  //   - persona   → customer name from config.json "persona" field (demo story character)
  //   - brandName → organisation name from the brand's DB brand_name column
  // If persona is not set, customer_name is omitted and the backend resolves
  // it from its own user store using the JWT.
  const configPersona = useSelector((state) => state.channel.persona);
  const orgName = useSelector((state) => state.channel.brandName || null);

  // Remote video track from the avatar agent participant
  const [avatarTrack, setAvatarTrack] = useState(null);

  // Warm-transfer UI state. Driven by `agent_transferring` events on the
  // `labb.agent.state` data topic. transferPhase = null when no transfer is
  // in progress; otherwise one of 'dialing' | 'briefing' | 'handing_off'.
  // agentHasLeft latches true once a transfer completes successfully so the
  // avatar tile stays torn down even if a stray late track event fires.
  const [transferPhase, setTransferPhase] = useState(null);
  const [agentHasLeft, setAgentHasLeft] = useState(false);
  // Ref mirror so the long-lived room.on(ParticipantDisconnected) handler
  // can read the current transferPhase without re-binding on every change.
  const transferPhaseRef = useRef(null);
  useEffect(() => { transferPhaseRef.current = transferPhase; }, [transferPhase]);

  // Real-time captions from agent transcription
  const [captionText, setCaptionText] = useState('');
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const captionSegmentsRef = useRef(new Map());  // id → text
  const captionTimeoutRef = useRef(null);
  const captionUpdateRef = useRef(null);  // debounce rapid updates

  // Refs
  const roomRef = useRef(null);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const onCallEndRef = useRef(onCallEnd);
  const applicationIdRef = useRef(applicationId);
  const brandIdRef = useRef(brandId);
  const ringingStartedRef = useRef(false);
  const sessionIdRef = useRef(null);  // NeuralWave session id, for release-on-hangup
  const videoHiddenRef = useRef(audioOnly);
  const enableSoundEffectsRef = useRef(enableSoundEffects);

  // Keep refs in sync
  useEffect(() => { onCallEndRef.current = onCallEnd; }, [onCallEnd]);
  useEffect(() => { applicationIdRef.current = applicationId; }, [applicationId]);
  useEffect(() => { brandIdRef.current = brandId; }, [brandId]);
  useEffect(() => { videoHiddenRef.current = videoHidden; }, [videoHidden]);
  useEffect(() => { enableSoundEffectsRef.current = enableSoundEffects; }, [enableSoundEffects]);

  // ── Subscribe / unsubscribe video tracks when videoHidden changes ─────
  useEffect(() => {
    const room = roomRef.current;
    if (!room || audioOnly) return;  // never touch video tracks in audioOnly mode

    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((pub) => {
        if (pub.kind !== Track.Kind.Video) return;
        if (videoHidden && pub.isSubscribed) {
          pub.setSubscribed(false);
          console.log(`🚫 VideoCall: Unsubscribing video from ${participant.identity}`);
        } else if (!videoHidden && !pub.isSubscribed) {
          pub.setSubscribed(true);
          console.log(`📺 VideoCall: Subscribing to video from ${participant.identity}`);
        }
      });
    });
  }, [videoHidden, audioOnly]);

  // ── Start ringing on mount ────────────────────────────────────────────
  useEffect(() => {
    if (enableSoundEffects && !ringingStartedRef.current) {
      ringingStartedRef.current = true;
      playRingingOutSound(true, false);
    }
    return () => {
      stopRingingSound();
    };
  }, [enableSoundEffects]);

  // ── Fetch token from backend ──────────────────────────────────────────
  const fetchToken = useCallback(async () => {
    // Direct LiveKit connection: when the config supplies BOTH a server url and a
    // token, skip the backend token service entirely (standalone non-Pega LiveKit).
    // NOTE: this embeds a token in config — use short-lived tokens; NEVER put the
    // LiveKit API secret in config (token minting must stay server-side).
    if (livekitConfig?.url && livekitConfig?.token) {
      return { url: livekitConfig.url, token: livekitConfig.token };
    }

    const jwt = await authService.getToken();

    // Session preferences — always tell the agent whether to start an avatar.
    // This is driven entirely by the component's audioOnly prop so the same
    // component can be used for both voice-only and video calls in the future.
    const sessionPrefs = {
      avatar: !audioOnly,   // false → agent skips avatar startup entirely
    };

    // Build personalization payload.
    //
    // customer_name resolution order:
    //   1. config.json "persona"       — demo story character name (e.g. "Sarah")
    //   2. Cognito given_name + family_name — real authenticated user
    //   3. null → omitted; backend resolves from its own user store via JWT
    //
    // org_name always comes from the brand's DB brand_name column.
    const cognitoFullName = userAttributes
      ? [userAttributes.given_name, userAttributes.family_name].filter(Boolean).join(' ') || null
      : null;
    const customerName = configPersona || cognitoFullName || null;

    const personalization = {
      ...(customerName && { customer_name: customerName }),
      ...(orgName && { org_name: orgName }),
      locale: selectedLocale || navigator.language || 'en-US', // user-selected; falls back to browser locale
      // Authenticated user extras — present only when logged in via Cognito
      ...(userAttributes?.email && { email: userAttributes.email }),
      ...(userAttributes?.phone_number && { phone_number: userAttributes.phone_number }),
      ...(userAttributes?.['custom:account_id'] && { account_id: userAttributes['custom:account_id'] }),
      ...(userAttributes?.['custom:policy_number'] && { policy_number: userAttributes['custom:policy_number'] }),
    };

    const headers = { 'Content-Type': 'application/json' };
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
    const sub = authService.getSub();
    if (sub) headers['X-Labb-FollowID'] = sub;
    // Opt-in header overrides for a custom token endpoint (e.g. a tenant API key
    // or bearer the Neuralwave voice/session route expects). Applied last so a
    // config can replace the default Cognito Authorization header. Only ever set
    // when a config supplies `livekit.headers`, so Pega/Cara defaults are untouched.
    if (livekitConfig?.headers) Object.assign(headers, livekitConfig.headers);

    // Obtain a Pega ticket so the LiveKit agent worker can authenticate with Pega.
    // Skipped for strictBody configs — the ticket would never be included in the
    // body, so there is no point making the request.
    let pegaTicket = null;
    if (!livekitConfig?.strictBody) try {
      const ticketParams = new URLSearchParams({ applicationId: applicationIdRef.current, channel: 'agent' });
      const ticketResp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/dxa-ticket?${ticketParams}`,
        { method: 'POST', headers }
      );
      if (ticketResp.ok) {
        const ticketData = await ticketResp.json();
        pegaTicket = ticketData.ticket;
      } else {
        console.warn('🎫 VideoCall: Failed to obtain Pega ticket:', ticketResp.status);
      }
    } catch (err) {
      console.warn('🎫 VideoCall: Pega ticket request error:', err);
    }

    // Request body.
    //
    //   • Default (no `livekit` block, or a `livekit.body` merge): Cara's normal
    //     payload — sessionPrefs + personalization (+ pega_ticket) — unchanged.
    //   • `livekit.strictBody: true`: send ONLY the fields in `livekit.body`, so a
    //     strict-schema endpoint (e.g. the Neuralwave voice/session route, which
    //     rejects unknown keys) receives exactly { presetId } and nothing else.
    const body = livekitConfig?.strictBody
      ? { ...(livekitConfig.body || {}) }
      : { ...sessionPrefs, personalization, ...(livekitConfig?.body || {}), ...(pegaTicket && { pega_ticket: pegaTicket }) };

    // Token endpoint: ONLY overridden when a config supplies `livekit.tokenUrl`.
    // Otherwise this is the exact original env default, so any config WITHOUT a
    // `livekit` block (all Pega configs, existing Cara configs) is unchanged.
    const tokenEndpoint = livekitConfig?.tokenUrl
      || `${process.env.REACT_APP_API_BASE_URL}/api/v1/agent/token?applicationId=${applicationIdRef.current}&brandId=${brandIdRef.current}`;

    const resp = await fetch(
      tokenEndpoint,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      }
    );
    if (!resp.ok) throw new Error(`Token request failed: ${resp.status}`);
    const data = await resp.json();
    // Custom-endpoint overrides — every one guarded on livekitConfig, so they are
    // no-ops for any config without a `livekit` block.
    if (livekitConfig && !data.url && data.livekitUrl) data.url = data.livekitUrl;
    if (livekitConfig?.url) data.url = livekitConfig.url;
    console.log('🎫 VideoCall: Token response:', {
      url: data.url,
      room_name: data.room_name,
      keys: Object.keys(data),
      sessionPrefs,
      personalization,
    });
    return data;
  }, [audioOnly, userAttributes, configPersona, orgName, selectedLocale, livekitConfig]);

  // ── Release the NeuralWave agent session on hang-up ───────────────────
  // No-op unless a config supplies `livekit.tokenUrl` AND the token response
  // carried a sessionId — so this never fires for Pega/default configs. The
  // release URL defaults to `<tokenUrl>/release` (overridable via
  // `livekit.releaseUrl`). Fire-and-forget with keepalive so it survives unmount.
  const releaseSession = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || !livekitConfig?.tokenUrl) return;
    sessionIdRef.current = null;  // guard against a double release
    const releaseUrl = livekitConfig.releaseUrl || `${livekitConfig.tokenUrl}/release`;
    try {
      const jwt = await authService.getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
      if (livekitConfig?.headers) Object.assign(headers, livekitConfig.headers);
      await fetch(releaseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId }),
        keepalive: true,
      });
    } catch (err) {
      console.warn('🎫 VideoCall: session release failed:', err);
    }
  }, [livekitConfig]);

  // ── End call ──────────────────────────────────────────────────────────
  const endCall = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    stopRingingSound();

    // Play end-call tone
    if (enableSoundEffects && endCallSound) {
      endCallSound.currentTime = 0;
      endCallSound.play().catch(() => {});
    }

    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    releaseSession();  // no-op unless a non-Pega livekit.tokenUrl session is active
    setStatus('ended');
    onCallEndRef.current?.();
  }, [enableSoundEffects, releaseSession]);

  // ── Toggle mic mute ───────────────────────────────────────────────────
  const toggleMic = useCallback(async () => {
    if (!roomRef.current) return;
    const newMuted = !micMuted;
    try {
      await roomRef.current.localParticipant.setMicrophoneEnabled(!newMuted);
      setMicMuted(newMuted);
      console.log(`🎤 VideoCall: Mic ${newMuted ? 'muted' : 'unmuted'}`);
    } catch (err) {
      console.error('Failed to toggle mic:', err);
    }
  }, [micMuted]);

  // ── Toggle speaker (mute/unmute all remote audio) ─────────────────────
  const toggleSpeaker = useCallback(() => {
    const newMuted = !speakerMuted;
    setSpeakerMuted(newMuted);
    // Mute/unmute all attached remote audio elements
    document.querySelectorAll('[id^="lk-audio-"]').forEach((el) => {
      el.muted = newMuted;
    });
    console.log(`🔊 VideoCall: Speaker ${newMuted ? 'muted' : 'unmuted'}`);
  }, [speakerMuted]);

  // ── Apply global mic device changes live ────────────────────────────
  useEffect(() => {
    const room = roomRef.current;
    if (!room || status !== 'connected') return;
    const applyMic = async () => {
      try {
        await room.localParticipant.setMicrophoneEnabled(true, {
          ...(globalMicId && globalMicId !== 'default'
            ? { deviceId: { exact: globalMicId } }
            : {}),
        });
        console.log(`🎤 VideoCall: Mic switched → ${globalMicId}`);
      } catch (err) {
        console.warn('Failed to switch mic device:', err);
      }
    };
    applyMic();
  }, [globalMicId, status]);

  // ── Apply global speaker device changes live ──────────────────────────
  useEffect(() => {
    if (status !== 'connected') return;
    const elements = document.querySelectorAll('[id^="lk-audio-"]');
    elements.forEach(async (el) => {
      if (el.setSinkId) {
        try {
          await el.setSinkId(globalSpeakerId || 'default');
          console.log(`🔊 VideoCall: Speaker switched → ${globalSpeakerId}`);
        } catch (err) {
          console.warn('Failed to set audio output device:', err);
        }
      }
    });
  }, [globalSpeakerId, status]);

  // ── Start the call duration timer ─────────────────────────────────────
  // Audio elements are never muted — the agent's first syllable (jingle or
  // greeting) plays the moment the track is attached. This handler only
  // transitions UI state from "ringing" to "connected".
  const ensureTimerStarted = useCallback(() => {
    if (!startTimeRef.current) {
      stopRingingSound();
      setStatus('connected');
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
  }, []);

  const ensureAudioReady = useCallback(() => {
    if (startTimeRef.current) return; // already answered
    console.log('🎧 VideoCall: Agent audio arrived — answering now');
    ensureTimerStarted();
  }, [ensureTimerStarted]);

  // ── Connect to LiveKit room (runs once on mount) ─────────────────────
  useEffect(() => {
    let cancelled = false;

    const connect = async () => {
      try {
        const data = await fetchToken();
        if (cancelled) return;

        // Remember the session id so we can release the agent on hang-up.
        // Only present for config-driven (non-Pega) sessions; null otherwise.
        sessionIdRef.current = data.sessionId || data.session_id || null;

        const { token, url } = data;

        const room = new Room({
          adaptiveStream: false,
          dynacast: true,
          // Always disable auto-subscribe so we control which tracks to receive.
          // Audio is always subscribed; video is subscribed based on videoHidden.
          autoSubscribe: false,
        });
        roomRef.current = room;

        // ── Manually subscribe to tracks as they're published ──
        room.on(RoomEvent.TrackPublished, (pub, participant) => {
          if (cancelled) return;
          if (participant.identity === room.localParticipant.identity) return;
          if (pub.kind === Track.Kind.Audio) {
            pub.setSubscribed(true);
            console.log(`🔊 VideoCall: Subscribing to audio from ${participant.identity}`);
          } else if (pub.kind === Track.Kind.Video) {
            // In audioOnly mode, never subscribe to video — avatar is disabled
            if (audioOnly) {
              console.log(`🚫 VideoCall: Ignoring video track from ${participant.identity} (audioOnly mode)`);
              return;
            }
            // Subscribe to video based on current videoHidden ref
            if (!videoHiddenRef.current) {
              pub.setSubscribed(true);
              console.log(`📺 VideoCall: Subscribing to video from ${participant.identity}`);
            } else {
              console.log(`🚫 VideoCall: Skipping video track from ${participant.identity} (video hidden)`);
            }
          }
        });

        // ── Participant events ──
        room.on(RoomEvent.ParticipantConnected, (p) => {
          console.log('🟢 VideoCall: Participant connected:', p.identity, {
            kind: p.kind,
            attributes: p.attributes,
            isAvatarWorker: !!p.attributes?.['lk.publish_on_behalf'],
          });
        });
        room.on(RoomEvent.ParticipantDisconnected, (p) => {
          console.log('🔴 VideoCall: Participant disconnected:', p.identity,
            'remaining remoteParticipants:', room.remoteParticipants.size);

          // Warm-transfer cleanup: LiveKit won't auto-disconnect rooms when
          // only one participant remains. Once every remote (agent + CSR)
          // has left, the customer is alone in an empty room — tear it down
          // and trigger the normal "call ended" UI flow.
          if (room.remoteParticipants.size === 0 && !cancelled) {
            console.log('📵 VideoCall: Room is empty after remote disconnect — ending call');
            endCall();
            return;
          }

          // Defensive fallback: if a remote leaves while a warm transfer is
          // still flagged as in-flight AND another remote remains (i.e. the
          // CSR), treat that as the agent having handed off successfully.
          // Without this, a missed/late `state: ended` event would leave the
          // "Transferring" indicator stuck on screen forever.
          if (transferPhaseRef.current && room.remoteParticipants.size > 0 && !cancelled) {
            console.log('🤝 VideoCall: Remote left mid-transfer with others remaining — treating as handoff complete');
            setTransferPhase(null);
            setAgentHasLeft(true);
            setAvatarTrack(null);
          }
        });

        // ── Track subscribed — handle audio + video from remote participants ──
        room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
          if (cancelled) return;
          const pid = participant.identity;
          console.log(`🎵 VideoCall: TrackSubscribed: ${track.kind} from ${pid}`, {
            sid: track.sid,
            source: pub.source,
          });

          // Skip local participant
          if (pid === room.localParticipant.identity) return;

          if (track.kind === Track.Kind.Audio) {
            const el = track.attach();
            el.id = `lk-audio-${pid}-${track.sid}`;
            el.muted = false;
            el.style.display = 'none';
            if (globalSpeakerId && globalSpeakerId !== 'default' && el.setSinkId) {
              el.setSinkId(globalSpeakerId).catch(() => {});
            }
            document.body.appendChild(el);
            console.log(`🔊 VideoCall: Audio attached (playing) for ${pid}`);
            ensureAudioReady();
          } else if (track.kind === Track.Kind.Video) {
            if (audioOnly) {
              // Should never arrive in audioOnly mode, but guard defensively
              console.log(`🚫 VideoCall: Ignoring subscribed video track from ${pid} (audioOnly mode)`);
              return;
            }
            console.log(`📺 VideoCall: Video track received from ${pid} — rendering avatar`);
            setAvatarTrack(track);
            // Avatar will fade in when AvatarView fires onReady (first rendered frame)
          }
        });

        // ── Track unsubscribed ──
        room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
          console.log(`🔇 VideoCall: TrackUnsubscribed: ${track.kind} from ${participant.identity}`);
          if (track.kind === Track.Kind.Video) {
            setAvatarTrack((prev) => (prev === track ? null : prev));
          }
          track.detach().forEach((el) => el.remove());
        });

        // ── Agent voice state via data messages ──
        // The agent publishes state changes on the "labb.agent.state" topic.
        // We also accept un-topiced messages for backwards compatibility.
        room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
          try {
            const msg = JSON.parse(new TextDecoder().decode(payload));
            if (topic && topic !== 'labb.agent.state') return;
            console.log(`${performance.now().toFixed(0)} ms — labb.agent.state:`, msg.type);
            if (msg.type === 'agent_speech_started') {
              setVoiceState('speaking');
              // Answer the call when agent starts speaking - this keeps ringing until agent speaks
              if (!startTimeRef.current) {
                console.log('🗣️ VideoCall: Agent started speaking — answering now');
                ensureAudioReady();
              }
            }
            else if (msg.type === 'agent_speech_stopped') setVoiceState('listening');
            else if (msg.type === 'agent_thinking') setVoiceState('thinking');
            else if (msg.type === 'agent_transferring') {
              // Single rolled-up event that drives the warm-transfer UI.
              // state='active' carries the current phase; state='ended'
              // carries the outcome. Only 'complete' means the agent (and
              // avatar) have left the room for good — other outcomes mean
              // the agent stayed with the customer.
              if (msg.state === 'active') {
                setTransferPhase(msg.phase || 'dialing');
              } else if (msg.state === 'ended') {
                setTransferPhase(null);
                if (msg.outcome === 'complete') {
                  setAgentHasLeft(true);
                  setAvatarTrack(null);
                }
              }
            }
          } catch { /* ignore non-JSON */ }
        });

        // ── Real-time captions from agent transcription ──
        room.on(RoomEvent.TranscriptionReceived, (segments, participant, _publication) => {
          if (cancelled) return;
          // Only show captions from remote participants (the agent)
          if (participant?.identity === room.localParticipant.identity) return;

          for (const seg of segments) {
            captionSegmentsRef.current.set(seg.id, seg.text);
          }

          // Debounce rapid updates to reduce jitter (batch within 80ms)
          if (captionUpdateRef.current) cancelAnimationFrame(captionUpdateRef.current);
          captionUpdateRef.current = requestAnimationFrame(() => {
            const fullText = Array.from(captionSegmentsRef.current.values()).join(' ');
            setCaptionText(fullText);
          });

          // Clear caption after a pause once the final segment arrives
          if (captionTimeoutRef.current) clearTimeout(captionTimeoutRef.current);
          const hasFinal = segments.some((s) => s.final);
          if (hasFinal) {
            captionTimeoutRef.current = setTimeout(() => {
              captionSegmentsRef.current.clear();
              setCaptionText('');
            }, 3000);
          }
        });

        room.on(RoomEvent.Disconnected, (reason) => {
          console.log('🔴 VideoCall: Disconnected:', reason);
          if (!cancelled) endCall();
        });

        // ── Connect + enable mic ──
        console.log('🔌 VideoCall: Connecting to', url);
        await room.connect(url, token);
        console.log('✅ VideoCall: Connected! Room:', room.name, 'Local:', room.localParticipant.identity);
        if (cancelled) { room.disconnect(); return; }

        // Enable mic — if a specific device was saved, try it first.
        // Fall back to the default mic if the saved device ID is stale
        // (e.g. headset unplugged) to avoid killing the whole call with
        // an OverconstrainedError.
        const preferredMic = globalMicId && globalMicId !== 'default' ? globalMicId : null;
        try {
          await room.localParticipant.setMicrophoneEnabled(true, {
            ...(preferredMic ? { deviceId: { exact: preferredMic } } : {}),
          });
          console.log(`🎤 VideoCall: Mic enabled${preferredMic ? ` (device: ${preferredMic})` : ' (default)'}`);
        } catch (micErr) {
          if (micErr.name === 'OverconstrainedError' || micErr.name === 'NotFoundError') {
            console.warn(`🎤 VideoCall: Saved mic device "${preferredMic}" unavailable (${micErr.name}), falling back to default`);
            await room.localParticipant.setMicrophoneEnabled(true);
            console.log('🎤 VideoCall: Mic enabled (default fallback)');
          } else {
            throw micErr; // Unexpected error (e.g. permission denied) — still fatal
          }
        }
        setVoiceState('listening');

        // ── Handle tracks from participants already in the room ──
        room.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((pub) => {
            // Subscribe to audio always; video only if not audioOnly and not hidden
            if (!pub.isSubscribed) {
              if (pub.kind === Track.Kind.Audio) {
                pub.setSubscribed(true);
              } else if (pub.kind === Track.Kind.Video && !audioOnly && !videoHiddenRef.current) {
                pub.setSubscribed(true);
              }
            }
            if (!pub.isSubscribed || !pub.track) return;
            const track = pub.track;

            if (track.kind === Track.Kind.Audio) {
              const el = track.attach();
              el.id = `lk-audio-${participant.identity}-${track.sid}`;
              el.muted = false;
              el.style.display = 'none';
              if (globalSpeakerId && globalSpeakerId !== 'default' && el.setSinkId) {
                el.setSinkId(globalSpeakerId).catch(() => {});
              }
              document.body.appendChild(el);
              console.log(`🔊 VideoCall: Audio attached (playing, existing) for ${participant.identity}`);
              ensureAudioReady();
            } else if (track.kind === Track.Kind.Video) {
              if (audioOnly) return;  // defensive — shouldn't be subscribed
              console.log(`📺 VideoCall: Video track (existing) from ${participant.identity}`);
              setAvatarTrack(track);
              // Don't answer yet — wait for AvatarView's onReady (first rendered frame)
            }
          });
        });

      } catch (err) {
        if (!cancelled) {
          console.error('❌ VideoCall: Connection failed:', err);
          stopRingingSound();
          setError(err.message);
          setStatus('ended');
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
      stopRingingSound();
      if (timerRef.current) clearInterval(timerRef.current);
      if (captionTimeoutRef.current) clearTimeout(captionTimeoutRef.current);
      if (captionUpdateRef.current) cancelAnimationFrame(captionUpdateRef.current);
      document.querySelectorAll('[id^="lk-audio-"]').forEach((el) => el.remove());
      // Play end-call tone if the call was connected (timer was running)
      if (startTimeRef.current && enableSoundEffectsRef.current && endCallSound) {
        endCallSound.currentTime = 0;
        endCallSound.play().catch(() => {});
      }
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
      releaseSession();  // covers unmount without an explicit endCall (guarded internally)
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Avatar callbacks ──────────────────────────────────────────────────
  const handleAvatarReady = useCallback(() => {
    console.log('🎭 VideoCall: Avatar is ready (first frame rendered) — answering call');
    setAvatarReady(true);
    // In video mode, THIS is the moment we answer: avatar is visually rendering,
    // so we stop ringing and unmute audio simultaneously — the user sees the avatar
    // and hears the greeting at the same time with no cut-off.
    ensureTimerStarted();
  }, [ensureTimerStarted]);

  const handleAvatarError = useCallback((err) => {
    console.error('🎭 VideoCall: Avatar error, falling back to voice-only:', err);
    setAvatarError(true);
    // Avatar failed — answer the call anyway so the user can still talk
    ensureTimerStarted();
  }, [ensureTimerStarted]);

  // Determine what to show.
  //  - `agentHasLeft` latches after a successful warm transfer so a late
  //    ParticipantConnected / TrackSubscribed event can't bring the avatar back.
  //  - `transferPhase` hides the avatar tile while a transfer is in flight,
  //    same as if the user had toggled "Hide Video" — we don't want the customer
  //    seeing the agent talking privately to the CSR. The avatar restores
  //    automatically when transferPhase clears (agent returned to customer).
  const showAvatar = !videoHidden && !!avatarTrack && !avatarError && !agentHasLeft && !transferPhase;
  const showVoiceOnly = videoHidden || !showAvatar || !avatarReady;

  // Voice state helpers (matching PhoneCallUI). Warm-transfer overrides the
  // voice state so the status reads as Transferring while it's in flight,
  // regardless of any agent_thinking / agent_speech_* events still firing.
  const getStatusColor = () => {
    if (transferPhase) return 'bg-white animate-pulse';
    switch (voiceState) {
      case 'thinking': return 'bg-yellow-400 animate-pulse';
      case 'listening': return 'bg-green-400 animate-pulse';
      case 'speaking': return 'bg-blue-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = () => {
    if (transferPhase) return 'Transferring';
    switch (voiceState) {
      case 'thinking': return 'Thinking';
      case 'listening': return 'Listening';
      case 'speaking': return 'Speaking';
      default: return 'Idle';
    }
  };

  // ── Render: Error ─────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="bg-gray-900 text-white h-full flex flex-col items-center justify-center p-6">
        <h1 className="text-xl font-semibold text-red-500">Connection failed</h1>
        <p className="text-sm text-gray-400 mt-2 max-w-xs text-center">{error}</p>
        <button
          onClick={endCall}
          className="mt-6 w-16 h-16 flex items-center justify-center bg-red-500 rounded-full hover:bg-red-600 transition-colors"
        >
          <Phone size={28} className="transform rotate-[135deg]" />
        </button>
      </div>
    );
  }

  // ── Render: Dialling / Connecting ─────────────────────────────────────

  if (status === 'connecting') {
    return (
      <div className="bg-gray-900 text-white h-full flex flex-col items-center justify-between p-6 overflow-auto">
        {/* Contact info — matches PhoneCallUI style */}
        <div className="text-center pt-8">
          <h1 className="text-4xl font-light">{contactName}</h1>
          <p className="text-gray-400 mt-1">Calling…</p>
        </div>

        {/* Spacer to push end button down */}
        <div className="h-[280px]"></div>

        {/* End call button */}
        <div className="w-full max-w-xs pb-4">
          <button
            onClick={endCall}
            className="w-16 h-16 flex items-center justify-center bg-red-500 rounded-full mx-auto hover:bg-red-600 transition-colors"
          >
            <Phone size={28} className="transform rotate-[135deg]" />
          </button>
        </div>
      </div>
    );
  }

  // ── Render: In-call ───────────────────────────────────────────────────

  return (
    <div className="relative w-full h-full bg-gray-900 text-white overflow-hidden" style={{ minHeight: '400px' }}>
      {/* Avatar video — rendered from remote video track published by agent.
        * Hidden during warm transfer via showAvatar (see gating above) — the
        * voice-only fallback takes over and the status badge reads "Transferring". */}
      {showAvatar && avatarTrack && (
        <div
          className="absolute inset-0 z-[1] transition-opacity duration-500"
          style={{ opacity: avatarReady ? 1 : 0 }}
        >
          <AvatarView
            videoTrack={avatarTrack}
            onReady={handleAvatarReady}
            onError={handleAvatarError}
          />
        </div>
      )}

      {/* Voice-only fallback — matches PhoneCallUI layout */}
      {showVoiceOnly && (
        <div className="absolute inset-0 z-[5] bg-gray-900 flex flex-col items-center p-6">
          {/* Contact info */}
          <div className="text-center pt-8">
            <h1 className="text-4xl font-light">{contactName}</h1>
            <p className="text-gray-400 mt-1">{formatDuration(duration)}</p>
            {/* AI status indicator */}
            <div className="mt-3 mb-2">
              <div className="flex items-center justify-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
                <p className="text-sm text-gray-400">{getStatusText()}</p>
              </div>
            </div>
            {showAvatar && !avatarReady && (
              <div className="flex items-center justify-center gap-1.5 mt-4">
                <span className="w-2 h-2 rounded-full bg-white/70 animate-typing-dot-1" />
                <span className="w-2 h-2 rounded-full bg-white/70 animate-typing-dot-2" />
                <span className="w-2 h-2 rounded-full bg-white/70 animate-typing-dot-3" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* "Working" overlay — light dim + typing indicator while agent executes
        * a tool. Hidden during a warm transfer so the arc overlay reads alone. */}
      <div
        className={`absolute inset-0 z-[8] flex items-end justify-center pb-44 pointer-events-none transition-all duration-500 ease-in-out ${
          avatarReady && showAvatar && voiceState === 'thinking' && !transferPhase
            ? 'opacity-100'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: avatarReady && showAvatar && voiceState === 'thinking' && !transferPhase ? 'rgba(0,0,0,0.5)' : 'transparent' }}
      >
        <div className="flex flex-col items-center gap-2.5 animate-fade-in">
          {/* Typing dots */}
          <div className="flex items-center gap-1.5 bg-black/50 backdrop-blur-md rounded-full px-4 py-2.5 shadow-lg">
            <span className="w-2 h-2 rounded-full bg-white/90 animate-typing-dot-1" />
            <span className="w-2 h-2 rounded-full bg-white/90 animate-typing-dot-2" />
            <span className="w-2 h-2 rounded-full bg-white/90 animate-typing-dot-3" />
          </div>
        </div>
      </div>

      {/* Overlay info when avatar is showing */}
      {avatarReady && showAvatar && (
        <div className="absolute top-4 left-4 bg-black/50 rounded-lg px-3.5 py-1.5 z-10">
          <p className="text-sm font-semibold">{contactName}</p>
          <p className="text-xs opacity-80">{formatDuration(duration)}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor()}`}></div>
            <p className="text-[11px] opacity-70">{getStatusText()}</p>
          </div>
        </div>
      )}

      {/* ── Real-time captions (single instance, always positioned above controls) ── */}
      <div
        className={`absolute bottom-28 left-4 right-4 z-20 flex justify-center pointer-events-none transition-all duration-500 ease-in-out ${
          captionText && captionsEnabled && status === 'connected'
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-2'
        }`}
      >
        <div className="bg-black/70 backdrop-blur-sm rounded-lg px-5 py-2.5 max-w-md shadow-lg">
          <p className="text-white text-sm text-center leading-relaxed transition-[text] duration-150 ease-out">
            {captionText || '\u00A0'}
          </p>
        </div>
      </div>

      {/* ── Bottom controls (single instance — never remounts) ── */}
      {/* Evenly spaced: buttons flex around the always-centred end-call button */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 w-full max-w-sm px-6">
        <div className="flex items-center justify-center gap-4 h-16">
            {/* Mic mute toggle */}
            <button
              onClick={toggleMic}
              className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 ${
                micMuted
                  ? 'bg-red-500/80 text-white hover:bg-red-500'
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
              }`}
              title={micMuted ? 'Unmute microphone' : 'Mute microphone'}
            >
              <span className="material-icons-outlined text-xl">
                {micMuted ? 'mic_off' : 'mic'}
              </span>
            </button>

            {/* Video on/off toggle — hidden in audioOnly mode (no avatar to show) */}
            {!audioOnly && (
              <button
                onClick={() => setVideoHidden((prev) => !prev)}
                className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 ${
                  videoHidden
                    ? 'bg-red-500/80 text-white hover:bg-red-500'
                    : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                }`}
                title={videoHidden ? 'Turn on video' : 'Turn off video'}
              >
                <span className="material-icons-outlined text-xl">
                  {videoHidden ? 'videocam_off' : 'videocam'}
                </span>
              </button>
            )}

          {/* End call — always dead centre */}
          <button
            onClick={endCall}
            className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-red-500 rounded-full hover:bg-red-600 transition-colors z-10"
          >
            <Phone size={28} className="transform rotate-[135deg]" />
          </button>

            {/* Speaker mute toggle */}
            <button
              onClick={toggleSpeaker}
              className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 ${
                speakerMuted
                  ? 'bg-red-500/80 text-white hover:bg-red-500'
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
              }`}
              title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
            >
              <span className="material-icons-outlined text-xl">
                {speakerMuted ? 'volume_off' : 'volume_up'}
              </span>
            </button>

            {/* Captions toggle — only in video mode */}
            {!audioOnly && (
              <button
                onClick={() => setCaptionsEnabled((prev) => !prev)}
                className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 ${
                  captionsEnabled
                    ? 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                    : 'bg-white/10 backdrop-blur-sm text-white/40 hover:bg-white/20'
                }`}
                title={captionsEnabled ? 'Turn off captions' : 'Turn on captions'}
              >
                <span className="material-icons-outlined text-xl">
                  {captionsEnabled ? 'closed_caption' : 'closed_caption_disabled'}
                </span>
              </button>
            )}
        </div>
      </div>
    </div>
  );
}